import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Store, ChevronLeft, ShieldCheck, Smile, X } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useLiveStreams, sellerLiveStream } from "../lib/useLive";

export default function ChatDialog({ open, onOpenChange, product, conversation, partnerName }) {
  const { user } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showProduct, setShowProduct] = useState(true);
  const [showPayments, setShowPayments] = useState(false);
  const bottomRef = useRef(null);
  const liveStreams = useLiveStreams();

  const partner = conversation?.partner_name || product?.supplier_name || partnerName || "Supplier";
  const company = conversation?.partner_company || product?.supplier_company || "";
  const avatar = conversation?.partner_avatar || product?.supplier_avatar || null;
  const storeId = product?.supplier_id || conversation?.supplier_id || conversation?.store_id || conversation?.store_key || null;
  const liveStream = sellerLiveStream(liveStreams, { id: storeId, name: partner });

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    let timer;
    const init = async () => {
      try {
        let c = conversation;
        if (!c && (product || partnerName)) {
          c = await api("/conversations", {
            method: "POST",
            auth: true,
            body: { partner_name: product?.supplier_name || partnerName, product_id: product?.id },
          });
        }
        if (cancelled || !c) return;
        setConv(c);
        const load = async () => {
          try {
            const full = await api(`/conversations/${c.id}`, { auth: true });
            if (!cancelled) setMessages(full.messages || []);
          } catch {}
        };
        await load();
        timer = setInterval(load, 5000);
      } catch (e) {
        toast.error("Could not open chat", { description: e.message });
      }
    };
    init();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, conversation?.id, product?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => {
    if (open) setShowProduct(Boolean(product));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  if (!open) return null;

  const pushText = async (body) => {
    if (!body.trim() || !conv) return false;
    try {
      await api(`/conversations/${conv.id}`, { method: "POST", auth: true, body: { text: body.trim() } });
      const full = await api(`/conversations/${conv.id}`, { auth: true });
      setMessages(full.messages || []);
      return true;
    } catch (err) {
      toast.error("Message not sent", { description: err.message });
      return false;
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !conv) return;
    setSending(true);
    const ok = await pushText(text);
    if (ok) setText("");
    setSending(false);
  };

  const sendTemplate = async (kind) => {
    if (!conv) return;
    const name = product?.title ? ` "${product.title}"` : "";
    const body =
      kind === "order"
        ? `${t("tpl_order")}${name}`
        : kind === "logistics"
        ? `${t("tpl_logistics")}${name}`
        : `${t("tpl_inquiry")}${name}`;
    if (await pushText(body)) {
      if (kind === "inquiry") setShowProduct(false);
      toast.success(t("msg_sent"));
    }
  };

  const close = () => onOpenChange(false);

  return (
    <div data-testid="chat-dialog" className="fixed inset-0 z-[70] flex flex-col bg-[#F2F2F4]">
      {/* Header — app style */}
      <header className="flex items-center gap-1 border-b border-slate-200/70 bg-white px-2 py-3 sm:px-4">
        <button data-testid="chat-back" onClick={close} aria-label="Back" className="rounded-full p-2 text-slate-800 transition hover:bg-slate-100 active:scale-90">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 truncate font-heading text-base font-extrabold text-slate-900">
            <span className="truncate">{partner}</span>
            {liveStream && (
              <span data-testid="chat-live-badge" className="flex shrink-0 items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
              </span>
            )}
          </p>
          {company && <p className="truncate text-[11px] text-slate-400">{company}</p>}
        </div>
        {storeId && (
          <Link
            data-testid="chat-store-link"
            to={`/store/${storeId}`}
            onClick={close}
            aria-label="Store"
            className="relative shrink-0 rounded-full p-2 text-slate-700 transition hover:bg-slate-100 active:scale-90"
          >
            <Store className="h-6 w-6" strokeWidth={1.7} />
            {liveStream && <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />}
          </Link>
        )}
      </header>

      {!user ? (
        <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
          <p className="text-sm text-slate-600">{t("login_chat")} — {partner}.</p>
          <button
            data-testid="chat-login-button"
            onClick={() => { close(); navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`); }}
            className="mt-4 rounded-full bg-teal-700 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-teal-800"
          >
            {t("nav_login")}
          </button>
        </div>
      ) : (
        <>
          {/* Messages area */}
          <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center pt-8">
                {avatar ? (
                  <img src={mediaUrl(avatar)} alt={partner} className="h-20 w-20 rounded-full object-cover" />
                ) : (
                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-teal-700/10 font-heading text-2xl font-extrabold text-teal-700">
                    {partner.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <p data-testid="chat-welcome" className="mt-3 font-heading text-lg font-bold text-slate-900">{t("chat_welcome")}</p>
                <div data-testid="chat-safety" className="mt-4 flex w-full max-w-md items-start gap-2.5 rounded-2xl bg-white p-4 shadow-sm">
                  <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-green-600" />
                  <p className="text-sm text-slate-700">
                    {t("chat_safety")}{" "}
                    <Link to="/help" onClick={close} className="font-semibold underline">{t("learn_more")}</Link>
                  </p>
                </div>
              </div>
            ) : (
              <div className="mx-auto max-w-2xl space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "me" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                        m.sender === "me" ? "rounded-br-sm bg-teal-700 text-white" : "rounded-bl-sm bg-white text-slate-800 shadow-sm"
                      }`}
                    >
                      {m.text}
                      <div className={`mt-0.5 text-[10px] ${m.sender === "me" ? "text-white/70" : "text-slate-400"}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Product inquiry card */}
          {product && showProduct && (
            <div data-testid="chat-product-card" className="border-t border-slate-200 bg-white px-4 pt-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{t("inquire_about")}</p>
                <button data-testid="chat-product-close" onClick={() => setShowProduct(false)} aria-label="Close" className="rounded-full p-1 text-slate-500 hover:bg-slate-100">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-2 flex items-center gap-3">
                {(product.thumb || product.image) && (
                  <img src={mediaUrl(product.thumb || product.image)} alt="" className="h-12 w-12 shrink-0 rounded-lg border border-slate-100 object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-base font-extrabold text-slate-900">
                    {fmt(product.price_min ?? product.price_max)}
                    {product.price_max && product.price_min && product.price_max !== product.price_min ? `–${fmt(product.price_max)}` : ""}
                  </p>
                  {product.promo && <p className="truncate text-xs font-semibold text-teal-600">{product.promo}</p>}
                </div>
                <button
                  data-testid="chat-product-send"
                  onClick={() => sendTemplate("inquiry")}
                  className="shrink-0 rounded-full bg-teal-700 px-6 py-2.5 text-sm font-bold text-white shadow-md shadow-teal-700/25 transition hover:bg-teal-800 active:scale-95"
                >
                  {t("send")}
                </button>
              </div>
              {/* quick action chips */}
              <div className="mt-3 flex gap-2 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <button data-testid="chip-order-request" onClick={() => sendTemplate("order")} className="shrink-0 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-teal-400 active:scale-95">
                  {t("chip_order_req")}
                </button>
                <button data-testid="chip-payment" onClick={() => setShowPayments((v) => !v)} className="shrink-0 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-teal-400 active:scale-95">
                  {t("chip_payment")}
                </button>
                {storeId && (
                  <Link data-testid="chip-minisite" to={`/store/${storeId}`} onClick={close} className="shrink-0 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-teal-400 active:scale-95">
                    {t("chip_minisite")}
                  </Link>
                )}
                <button data-testid="chip-logistics" onClick={() => sendTemplate("logistics")} className="shrink-0 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-teal-400 active:scale-95">
                  {t("chip_logistics")}
                </button>
              </div>
              {showPayments && (
                <div data-testid="payment-options-panel" className="mb-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                  <p className="font-bold text-slate-800">{t("chip_payment")}</p>
                  <p className="mt-1">M-Pesa · Card · Snippe</p>
                </div>
              )}
            </div>
          )}

          {/* Input bar */}
          <form onSubmit={send} className="flex items-center gap-2 border-t border-slate-200 bg-white px-3 py-3">
            <button
              data-testid="chat-emoji"
              type="button"
              onClick={() => setText((v) => (v + " 🙂").trimStart())}
              aria-label="Emoji"
              className="shrink-0 rounded-full p-1.5 text-slate-500 transition hover:bg-slate-100 active:scale-90"
            >
              <Smile className="h-6 w-6" strokeWidth={1.7} />
            </button>
            <input
              data-testid="chat-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={1000}
              placeholder={t("enter_msg")}
              className="flex-1 rounded-full bg-slate-100 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-500/30"
            />
            <button
              data-testid="chat-send-button"
              type="submit"
              disabled={sending || !text.trim()}
              className="shrink-0 rounded-full bg-teal-700 p-2.5 text-white transition hover:bg-teal-800 active:scale-95 disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </>
      )}
    </div>
  );
}
